import React from 'react';
import PropTypes from 'prop-types';
import { Dropdown, Input } from '../../../../../common/ui';
import FilterModal from '../../../../../common/components/FilterModal';
import FilterDateRangePicker from '../../../../../common/components/FilterDateRangePicker';
import styles from './styles.module.css';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'settled', label: 'Settled' },
  { value: 'defaulted', label: 'Defaulted' },
  { value: 'repossessed', label: 'Repossessed' },
  { value: 'settled_early', label: 'Settled Early' },
];

const AGREEMENT_DATE_FILTER_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'startDate', label: 'Agreement Start Date' },
  { value: 'closureDate', label: 'Closure Date' },
  { value: 'maturityDate', label: 'Maturity Date' },
];

const AGREEMENT_DATE_FILTER_LABELS = {
  startDate: 'Start Date',
  closureDate: 'Closure Date',
  maturityDate: 'Maturity Date',
};

const EMPTY_DATE_RANGE = { startDate: null, endDate: null };

const FilterComp = ({
  selectedStatus,
  setSelectedStatus,
  selectedAgreementDateType,
  setSelectedAgreementDateType,
  selectedAgreementDateRange,
  setSelectedAgreementDateRange,
  selectedDueDateRange,
  setSelectedDueDateRange,
  selectedHpPlanId,
  setSelectedHpPlanId,
  selectedOverdueAmountMin,
  setSelectedOverdueAmountMin,
  selectedOverdueAmountMax,
  setSelectedOverdueAmountMax,
  hpPlanOptions,
  onApply,
}) => (
  <FilterModal title="Filter by" onApply={onApply}>
    <div className={styles.filterOptionName}>Agreement Status</div>
    <Dropdown
      id="hp-filter-status"
      options={STATUS_OPTIONS}
      value={selectedStatus}
      onChange={setSelectedStatus}
    />

    <div className={styles.spacer} />

    <div className={styles.filterOptionName}>HP Plan</div>
    <Dropdown
      id="hp-filter-plan"
      options={[{ value: '', label: 'All Plans' }, ...hpPlanOptions]}
      value={selectedHpPlanId}
      onChange={setSelectedHpPlanId}
    />

    <div className={styles.spacer} />

    <div className={styles.filterOptionName}>Overdue Amount</div>
    <div className={styles.amountRangeRow}>
      <Input
        id="hp-filter-overdue-min"
        label="Min"
        type="number"
        value={selectedOverdueAmountMin}
        onChange={setSelectedOverdueAmountMin}
        placeholder="0.00"
        className={styles.amountRangeInput}
      />
      <Input
        id="hp-filter-overdue-max"
        label="Max"
        type="number"
        value={selectedOverdueAmountMax}
        onChange={setSelectedOverdueAmountMax}
        placeholder="0.00"
        className={styles.amountRangeInput}
      />
    </div>

    <div className={styles.spacer} />

    <div className={styles.filterOptionName}>Agreement Filter Dates</div>
    <Dropdown
      id="hp-filter-agreement-date-type"
      options={AGREEMENT_DATE_FILTER_OPTIONS}
      value={selectedAgreementDateType}
      onChange={(value) => {
        setSelectedAgreementDateType(value);
        if (!value) {
          setSelectedAgreementDateRange(EMPTY_DATE_RANGE);
        }
      }}
    />
    {selectedAgreementDateType ? (
      <>
        <div className={styles.spacer} />
        <div className={styles.filterOptionName}>
          {AGREEMENT_DATE_FILTER_LABELS[selectedAgreementDateType]} Range
        </div>
        <FilterDateRangePicker
          key={selectedAgreementDateType}
          className={styles.filterDateRange}
          initialStartDate={selectedAgreementDateRange.startDate}
          initialEndDate={selectedAgreementDateRange.endDate}
          onDateRangeChange={(range) => {
            setSelectedAgreementDateRange({
              startDate: range?.startDate || null,
              endDate: range?.endDate || null,
            });
          }}
        />
      </>
    ) : null}

    <div className={styles.spacer} />

    <div className={styles.filterOptionName}>Due Date</div>
    <FilterDateRangePicker
      className={styles.filterDateRange}
      initialStartDate={selectedDueDateRange.startDate}
      initialEndDate={selectedDueDateRange.endDate}
      onDateRangeChange={(range) => {
        setSelectedDueDateRange({
          startDate: range?.startDate || null,
          endDate: range?.endDate || null,
        });
      }}
    />
  </FilterModal>
);

FilterComp.propTypes = {
  selectedStatus: PropTypes.string.isRequired,
  setSelectedStatus: PropTypes.func.isRequired,
  selectedAgreementDateType: PropTypes.string.isRequired,
  setSelectedAgreementDateType: PropTypes.func.isRequired,
  selectedAgreementDateRange: PropTypes.shape({
    startDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
    endDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
  }).isRequired,
  setSelectedAgreementDateRange: PropTypes.func.isRequired,
  selectedDueDateRange: PropTypes.shape({
    startDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
    endDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
  }).isRequired,
  setSelectedDueDateRange: PropTypes.func.isRequired,
  selectedHpPlanId: PropTypes.string.isRequired,
  setSelectedHpPlanId: PropTypes.func.isRequired,
  selectedOverdueAmountMin: PropTypes.string.isRequired,
  setSelectedOverdueAmountMin: PropTypes.func.isRequired,
  selectedOverdueAmountMax: PropTypes.string.isRequired,
  setSelectedOverdueAmountMax: PropTypes.func.isRequired,
  hpPlanOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })
  ).isRequired,
  onApply: PropTypes.func.isRequired,
};

export default FilterComp;
